<script setup lang="ts">
import DialogCommon from "@/components/Common/DialogCommon.vue"
import { defineAsyncComponent, onMounted, ref } from "vue"
import { useGlobalStore } from "@/stores/global"
import { useCurrentAttackerStore } from "@/stores/currentAttacker"
import { useCurrentDefenderStore } from "@/stores/currentDefender"
import { useModifiersGlobalStore } from "@/stores/modifiersGlobal"
import { useUpdateBackupStore } from "@/stores/updateBackup"
import { useUpdateManagementStore } from "@/stores/updateManagement"
import * as Sentry from "@sentry/nuxt"

interface Props {
  applyAllUpdates?: boolean
  applyLatestUpdate?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  applyAllUpdates: false,
  applyLatestUpdate: false,
})

const UpdateError = defineAsyncComponent(
  () => import("@/components/Init/UpdateError.vue")
)

const storeGlobal = useGlobalStore()
const currentAttackerStore = useCurrentAttackerStore()
const currentDefenderStore = useCurrentDefenderStore()
const storeModifiersGlobal = useModifiersGlobalStore()
const storeUpdateBackup = useUpdateBackupStore()
const updateManagementStore = useUpdateManagementStore()
const { $toast } = useNuxtApp()

const error = ref<Error | null>(null)
const showDialog = ref<boolean>(true)

function runUpdates(): void {
  storeUpdateBackup.backupData()

  const versionToUpdateFrom: string =
    updateManagementStore.versionToUpdateFrom || storeGlobal.appVersionLastSeen
  console.log("Version to update from", versionToUpdateFrom)

  try {
    const updateOptions = {
      applyAllUpdates: props.applyAllUpdates,
      applyLatestUpdate: props.applyLatestUpdate,
      versionToUpdateFrom,
    }
    // TODO: This is a bit specific to a single migration (0.72.0),
    //  but I needed a way to ensure that the current attacker/defender had the
    //  setCurrentAttacker() / setCurrentDefender() action run on them.
    //  An better approach would be to only clear these properties if they
    //  fail a zod validation check?
    if (typeof currentAttackerStore.currentAttacker.sim === "undefined") {
      currentAttackerStore.currentAttacker = {}
    }
    if (typeof currentDefenderStore.currentDefender.sim === "undefined") {
      currentDefenderStore.currentDefender = {}
    }

    console.log("Updating global modifiers...")
    storeModifiersGlobal.updateModifiersGlobal(updateOptions)

    console.log("Updating profiles...")
    updateManagementStore.updateProfiles(updateOptions)
    updateManagementStore.versionToUpdateFrom = null

    $toast.success("Update successful.")
  } catch (err) {
    console.error("❌ Update process failed:", err)

    // Report error to Sentry with additional context
    Sentry.withScope((scope) => {
      scope.setTag("error_type", "update_failure")
      scope.setContext("update_context", {
        versionToUpdateFrom,
        applyAllUpdates: props.applyAllUpdates,
        applyLatestUpdate: props.applyLatestUpdate,
        profilesCount: storeGlobal.profiles?.length || 0,
        hasCurrentAttacker: Boolean(currentAttackerStore.currentAttacker),
        hasCurrentDefender: Boolean(currentDefenderStore.currentDefender),
      })
      Sentry.captureException(err)
    })

    error.value = err as Error
    // Restore backup data if there was an error,
    // otherwise the error could be different every time.
    storeUpdateBackup.restoreBackupData()
    $toast.error("Update failed. Data has been restored from backup.")
  }

  if (!error.value) {
    // Only clear backup data once we've applied all updates without error.
    storeUpdateBackup.clearBackupData()
    storeGlobal.updateCurrentProfiles()
    storeGlobal.syncAppVersions()
    updateManagementStore.updateLastSeen()
    updateManagementStore.updateInProgress = false
    storeGlobal.updateInProgress = false
  } else {
    // Don't close the modal if there was an error
    storeGlobal.updateInProgress = true
    updateManagementStore.updateInProgress = true
  }
}

onMounted(() => {
  runUpdates()
})
</script>

<template>
  <DialogCommon v-model="showDialog" max-width="700px" :persistent="true">
    <UpdateError v-if="error" :error="error" class="wrapper" />
    <div v-else class="wrapper no-update-error">
      <h2 class="mb-2">Updating local data</h2>
      <div class="mb-4"><small>PLEASE HOLD</small></div>
      <v-progress-linear :indeterminate="true" color="primary" />
    </div>
  </DialogCommon>
</template>

<style lang="scss" scoped>
.wrapper {
  background-color: #1e1e1e;
  padding: 2rem;
  & > *:last-child {
    margin-bottom: 0;
  }
}
.wrapper.no-update-error {
  text-align: center;
}
</style>
